import { Fragment, useState } from 'react';
import ReceiptHiddenSection from './ReceiptHiddenSection';
import {
  KoobBadge,
  KoobInputText,
  KoobParagraph,
  KoobRichTextEditor,
  KoobTitle,
} from '@koob/ui';
import { useTranslation } from 'react-i18next';
import { ButtonReset } from '@koob/margaret';
import DOMPurify from 'dompurify';

const isDefaultContent = (value) => {
  const defaultContents = [
    '<p><br></p>',
    '<h1><br></h1>',
    '<h2><br></h2>',
    '<h3><br></h3>',
    '<p></p>',
  ];

  return defaultContents.includes(value);
};

export default function ReceiptNotes({
  additionalInformations,
  setAdditionalInformations,
}) {
  const { t } = useTranslation('trips');
  const [activeIndex, setActiveIndex] = useState(0);
  const activeAdditionalInfos = additionalInformations[activeIndex];

  const addNewAdditionalInformation = () => {
    const next = [
      ...additionalInformations,
      {
        id: crypto.randomUUID(),
        title: t('receipt.notes.new'),
        isGeneric: true,
        notes: '',
      },
    ];
    setAdditionalInformations(next);
    setActiveIndex(additionalInformations.length);
  };

  const updateActiveAdditionalInformation = (key, value) => {
    setAdditionalInformations(
      additionalInformations.map((info, index) =>
        index === activeIndex ? { ...info, [key]: value } : info,
      ),
    );
  };

  const removeAdditionalInformation = (idToRemove) => {
    setAdditionalInformations(
      additionalInformations.filter(({ id }) => id !== idToRemove),
    );
  };

  const handleActiveIndexChange = (newIndex) => {
    setActiveIndex(newIndex);
  };

  return (
    <>
      {additionalInformations.map(({ id, title, notes }) => (
        <div
          key={id}
          className={[
            'w-full',
            isDefaultContent(notes) || notes.length === 0
              ? 'export-hidden'
              : '',
          ].join(' ')}
        >
          <KoobTitle>
            {(isDefaultContent(notes) || notes.length === 0) && (
              <i className="fa fa-eye-slash mr-1.5" />
            )}
            {title}
          </KoobTitle>
          <div className="my-5 text-gray-500">
            <div
              className="prose prose-sm w-full max-w-full"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notes) }}
            />
          </div>
        </div>
      ))}

      <ReceiptHiddenSection>
        <KoobTitle size="md">
          <div className="flex space-x-3 items-center">
            <div>{t('receipt.notes.title')}</div>
            <KoobBadge>{t('receipt.optional')}</KoobBadge>
          </div>
        </KoobTitle>
        <div className="flex flex-wrap space-x-2 my-2">
          {additionalInformations.map(({ id, title }, index) => (
            <button
              key={id}
              className={[
                'border rounded-md shadow-xl p-2 font-medium',
                index === activeIndex
                  ? 'bg-orange-100 border-orange-500 text-orange-500'
                  : 'border-gray-300',
              ].join(' ')}
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handleActiveIndexChange(index);
              }}
            >
              <span>{title}</span>
              {index !== 0 && (
                <ButtonReset
                  style={{
                    marginLeft: '0.5rem',
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (activeIndex >= index && activeIndex > 0) {
                      setActiveIndex(activeIndex - 1);
                    }
                    removeAdditionalInformation(id);
                  }}
                >
                  <i className="fa-solid fa-trash-can" />
                </ButtonReset>
              )}
            </button>
          ))}
          <ButtonReset
            className="bg-orange-500"
            onClick={() => addNewAdditionalInformation()}
          >
            <i className="fa-light fa-circle-plus text-base" />
          </ButtonReset>
        </div>
        <KoobParagraph size="sm">
          {t('receipt.notes.description1')}
        </KoobParagraph>

        <Fragment key={activeIndex}>
          <div className="mt-3">
            <KoobInputText
              value={activeAdditionalInfos?.title ?? ''}
              onChange={(e) =>
                updateActiveAdditionalInformation('title', e.target.value)
              }
              placeholder="e.g. Additional Information"
            />
          </div>
          <div className="mt-1">
            <KoobRichTextEditor
              key={`${activeIndex}-${activeAdditionalInfos?.id}`}
              value={activeAdditionalInfos?.notes ?? ''}
              onChange={(value) =>
                updateActiveAdditionalInformation('notes', value)
              }
              placeholder="e.g. Please note that the hotel does not accept pets."
            />
          </div>
        </Fragment>
      </ReceiptHiddenSection>
    </>
  );
}
